// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { css } from '@emotion/css';
import { Theme, useTheme, Tooltip } from '@mui/material';
import { BiSolidMoon, BiSolidSun } from 'react-icons/bi';
import { MdFullscreen, MdFullscreenExit, MdVisibility, MdVisibilityOff } from 'react-icons/md';

import GoogleMapsElement from './GoogleMapsElement';
import { useMapPreferences } from './useMapPreferences';

// TYPES ---------------------------------------------------------------- //

interface MapControlsProps {
  style?: React.CSSProperties;
  className?: string;
}

// Vendor-prefixed Fullscreen API wrappers (Safari).
type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
};
type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void>;
};

const getFullscreenElement = (): Element | null => {
  const doc = document as FullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
};

const requestFullscreenOn = (el: HTMLElement) => {
  const candidate = el as FullscreenElement;
  if (candidate.requestFullscreen) return candidate.requestFullscreen();
  if (candidate.webkitRequestFullscreen) return candidate.webkitRequestFullscreen();
};

const exitFullscreen = () => {
  const doc = document as FullscreenDocument;
  if (doc.exitFullscreen) return doc.exitFullscreen();
  if (doc.webkitExitFullscreen) return doc.webkitExitFullscreen();
};

// COMPONENT ---------------------------------------------------------------- //

const MapControls = ({ style, className }: MapControlsProps) => {
  const theme = useTheme();
  const { mode, spotlight, setMode, setSpotlight } = useMapPreferences();
  const cls = useStyles(theme, mode);

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  // Sync local fullscreen state with the document.
  React.useEffect(() => {
    const sync = () => {
      const container = wrapperRef.current?.parentElement ?? null;
      setIsFullscreen(!!container && getFullscreenElement() === container);
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    sync();
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  const handleFullscreen = () => {
    const container = wrapperRef.current?.parentElement;
    if (!container) return;
    if (getFullscreenElement() === container) {
      exitFullscreen();
    } else {
      requestFullscreenOn(container);
    }
  };

  const isDark = mode === 'dark';

  // Default position: top right, near Google's native control zone.
  const positionStyle: React.CSSProperties = { top: 10, right: 10, ...style };

  return (
    <GoogleMapsElement ref={wrapperRef} style={positionStyle} className={className} disablePadding>
      <div className={cls.stack}>
        <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} placement='left'>
          <button
            type='button'
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            aria-pressed={isFullscreen}
            onClick={handleFullscreen}
            className={`${cls.btn} ${isFullscreen ? cls.btnActive : cls.btnInactive}`}
          >
            {isFullscreen ? <MdFullscreenExit size={20} /> : <MdFullscreen size={20} />}
          </button>
        </Tooltip>
        <Tooltip title={isDark ? 'Switch to light mode' : 'Switch to dark mode'} placement='left'>
          <button
            type='button'
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-pressed={isDark}
            onClick={() => setMode(isDark ? 'light' : 'dark')}
            className={`${cls.btn} ${isDark ? cls.btnActive : cls.btnInactive}`}
          >
            {isDark ? <BiSolidMoon size={18} /> : <BiSolidSun size={18} />}
          </button>
        </Tooltip>
        <Tooltip title={spotlight ? 'Disable spotlight mode' : 'Enable spotlight mode'} placement='left'>
          <button
            type='button'
            aria-label={spotlight ? 'Disable spotlight mode' : 'Enable spotlight mode'}
            aria-pressed={spotlight}
            onClick={() => setSpotlight(!spotlight)}
            className={`${cls.btn} ${spotlight ? cls.btnActive : cls.btnInactive}`}
          >
            {spotlight ? <MdVisibility size={18} /> : <MdVisibilityOff size={18} />}
          </button>
        </Tooltip>
      </div>
    </GoogleMapsElement>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme, mode: 'light' | 'dark') => {
  const primaryMain = theme?.palette?.primary?.main || '#1976d2';
  const primaryFaded = `${primaryMain}20`;
  const isLight = mode === 'light';
  const inactiveFg = isLight
    ? theme?.palette?.text?.secondary || 'rgba(0, 0, 0, 0.55)'
    : theme?.palette?.text?.contrastSecondary || 'rgba(255, 255, 255, 0.7)';
  const inactiveHoverBg = isLight
    ? theme?.palette?.action?.hover || 'rgba(0, 0, 0, 0.04)'
    : 'rgba(255, 255, 255, 0.08)';

  return {
    stack: css`
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding: 4px;
    `,
    btn: css`
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      padding: 0;
      border: none;
      background: transparent;
      border-radius: 6px;
      cursor: pointer;
      transition: background-color 150ms ease, color 150ms ease;
    `,
    btnActive: css`
      background-color: ${primaryFaded};
      color: ${primaryMain};

      &:hover {
        background-color: ${primaryFaded};
      }
    `,
    btnInactive: css`
      color: ${inactiveFg};

      &:hover {
        background-color: ${inactiveHoverBg};
      }
    `,
  };
};

// EXPORT ---------------------------------------------------------------- //

export default MapControls;
